import { useCallback, useEffect, useState } from 'react'
import { ApproverSettings, NewDeal, Product, UseApproverSettingsResult, User } from 'types'
import { useGlobalApproverSettings } from '../../api/aws/approvers/fetchGlobalApprovers'
import { useUsers } from '../../api/aws/useUsers'
import { approverIdsForDeal, INITIAL_STATE } from './types'

export const useApproverSettings = (initial: ApproverSettings = INITIAL_STATE): UseApproverSettingsResult => {
  const { users = [], isUsersLoading } = useUsers(true, ['approverSettings', 'users']) as {
    users: User[]
    isUsersLoading: boolean
  }
  const { data: globalApprovers, isLoading } = useGlobalApproverSettings(true)
  const [isApproverSettingsOpen, setIsApproverSettingsOpen] = useState(false)

  const [creativeAdditions, setCreativeAdditions] = useState<string[]>([])
  const [msaAdditions, setMsaAdditions] = useState<string[]>([])
  const [seoAdditions, setSeoAdditions] = useState<string[]>([])
  const [websiteAdditions, setWebsiteAdditions] = useState<string[]>([])
  const [housingAdditions, setHousingAdditions] = useState<string[]>([])

  useEffect(() => {
    if (!isLoading && globalApprovers) {
      setCreativeAdditions(globalApprovers.creative || [])
      setMsaAdditions(globalApprovers.msa || [])
      setSeoAdditions(globalApprovers.seo || [])
      setWebsiteAdditions(globalApprovers.website || [])
      setHousingAdditions(globalApprovers.housing || []) // ✅ add this line
    }
  }, [isLoading, globalApprovers])

  const handleAddApprover = (type: keyof ApproverSettings, value: string[]) => {
    switch (type) {
      case 'creative':
        setCreativeAdditions(value)
        break
      case 'msa':
        setMsaAdditions(value)
        break
      case 'seo':
        setSeoAdditions(value)
        break
      case 'website':
        setWebsiteAdditions(value)
        break
      case 'housing':
        setHousingAdditions(value)
        break
    }
  }

  const getMergedApprovers = (): ApproverSettings => ({
    creative: Array.from(new Set([...(initial.creative ?? []), ...creativeAdditions])),
    msa: Array.from(new Set([...(initial.msa ?? []), ...msaAdditions])),
    seo: Array.from(new Set([...(initial.seo ?? []), ...seoAdditions])),
    website: Array.from(new Set([...(initial.website ?? []), ...websiteAdditions])),
    housing: Array.from(new Set([...(initial.housing ?? []), ...housingAdditions])),
  })

  const resetApproverAdditions = () => {
    setCreativeAdditions([])
    setMsaAdditions([])
    setSeoAdditions([])
    setWebsiteAdditions([])
    setHousingAdditions([])
  }

  const handleOpenApproversSettings = () => {
    setIsApproverSettingsOpen(true)
  }

  const handleCloseApproversSettings = () => {
    setIsApproverSettingsOpen(false)
    resetApproverAdditions()
  }

  const determineApprovers = useCallback(
    (deal: NewDeal, products: Product[], globalApprovers: ApproverSettings) => {
      if (!deal || !deal.type || !Array.isArray(products)) return []

      const uniqueIds = approverIdsForDeal(
        deal.type,
        products.map((product: Product) => product?.category || ''),
        globalApprovers
      )

      // // Debug: Log to help identify ID mismatches
      // if (uniqueIds.length > 0 && users?.length > 0) {
      //   console.log('[determineApprovers] Looking for approver IDs:', uniqueIds)
      //   console.log('[determineApprovers] Sample user_uuids from users:', users.slice(0, 3).map(u => u.user_uuid))
      // }

      const finalApprovers = uniqueIds
        .map(id => {
          const user = Array.isArray(users) ? users.find(u => u.user_uuid === id) : null
          if (!user && id) {
            console.warn(`[determineApprovers] No user found for approver ID: ${id}`)
          }
          return user
            ? {
                id: user.user_uuid,
                name: user.name,
                image: user.image_512,
                hasApproved: false,
                hasRejected: false,
              }
            : null
        })
        .filter(Boolean)

      // console.log('[determineApprovers] Final approvers:', finalApprovers)
      return finalApprovers
    },
    [users]
  )

  return {
    users,
    isUsersLoading,
    approvers: {
      creative: creativeAdditions,
      msa: msaAdditions,
      seo: seoAdditions,
      website: websiteAdditions,
      housing: housingAdditions,
    },
    handleAddApprover,
    getMergedApprovers,
    handleOpenApproversSettings,
    handleCloseApproversSettings,
    isApproverSettingsOpen,
    determineApprovers,
    globalApprovers,
  }
}
